import { JobLogVerbosity, useAppConfiguration, useRepositoryMutation } from '@mews-commander/data-access';
import { useEnterpriseNavigate } from '@mews-commander/ui';
import { PlatformEnvironment } from '@mews/enums';
import {
    DateTimeUtils,
    lt,
    showSuccessAlert,
    TimeService,
    useAppNowUtc,
    useDateTimeLocalizationInfo,
} from '@mews/framework';

// ISO minute constant that is not expected to change in the future
const TIMEZONE_IDENTIFIER = 'GMT';

const JOB_TYPE = 'EnterpriseReservationsCanceller';

enum JobExecutionStates {
    Error = 'JobExecutionStates.Error',
    InProgress = 'JobExecutionStates.InProgress',
    Success = 'JobExecutionStates.Success',
    Timeout = 'JobExecutionStates.Timeout',
    Warning = 'JobExecutionStates.Warning',
}

const defaultQueryParameters = {
    Custom: true,
    [JobExecutionStates.Error]: true,
    [JobExecutionStates.InProgress]: true,
    [JobExecutionStates.Success]: true,
    [JobExecutionStates.Timeout]: true,
    [JobExecutionStates.Warning]: true,
    'JobExecutorType.Id': JOB_TYPE,
};

interface UseCancelReservationsProps {
    enterpriseId: string;
    enterpriseName: string;
}

export const useCancelReservations = ({ enterpriseId, enterpriseName }: UseCancelReservationsProps) => {
    const { mutate, isPending, isSuccess } = useRepositoryMutation('jobs', 'create');
    const navigate = useEnterpriseNavigate();
    const isProduction = useAppConfiguration().environment === PlatformEnvironment.Production;
    const localizationInfo = useDateTimeLocalizationInfo();
    const appNowUtc = useAppNowUtc().value;
    const utcDate = DateTimeUtils.dateTimeUtcStringToDate(appNowUtc, TIMEZONE_IDENTIFIER).value;
    const utcTime = DateTimeUtils.dateTimeUtcStringToTime(appNowUtc, TIMEZONE_IDENTIFIER);
    const shortUtcTime = TimeService.toShortString(utcTime, localizationInfo);
    const jobName = `${enterpriseName}_${utcDate}_${shortUtcTime}`;

    const getMutationVariables = (details: string) => ({
        jobs: [
            {
                name: jobName,
                executorTypeName: JOB_TYPE,
                startUtc: appNowUtc,
                period: 'PT30S',
                maxExecutionTime: 'PT45M',
                logVerbosity: JobLogVerbosity.Everything,
                options: {
                    parallelExecution: false,
                    timeoutRetryDisabled: false,
                },
                data: JSON.stringify({
                    enterpriseId,
                    iterations: isProduction ? 25 : 5,
                    iterationSize: 2000,
                    cancellationReasonDetail: details,
                }),
            },
        ],
    });

    const onSuccess = () => {
        showSuccessAlert({
            title: lt('CancelReservationsJobTitle'),
            description: lt('CancelReservationsJobDescription'),
        });

        void navigate({
            to: 'JobExecution/Index',
            search: {
                ...defaultQueryParameters,
                'StartUtc.Date': utcDate,
                'StartUtc.Time': shortUtcTime,
                Name: jobName,
            },
        });
    };

    const handleCancelReservations = (details: string) => {
        mutate(getMutationVariables(details), { onSuccess });
    };

    return {
        handleCancelReservations,
        isPending,
        isSuccess,
    };
};
